import { slugify } from './text'
import { variantDisplayNames } from '../data/guide/variantDisplayNames'
import type { Monster, WorldData } from '../types/world'

/**
 * Rutas legibles por categoría y nombre:
 *   /guia/items/joyas/jewel-of-bless
 *   /guia/monstruos/bull-fighter   ·   /guia/npc/sebina-the-priest
 *   /guia/mapas/lorencia           ·   /guia/mapas/lorencia/bull-fighter
 *
 * Se calculan una vez al cargar los archivos. Si dos entidades comparten nombre en la misma categoría,
 * se añade un sufijo para distinguirlas. Las rutas antiguas por ID siguen funcionando y redirigen aquí.
 */

/** Categoría pública por sección del ítem (primer número del ID). */
export const ITEM_SECTION_SLUG: Record<number, string> = {
  0: 'espadas',
  1: 'hachas',
  2: 'mazas',
  3: 'lanzas',
  4: 'arcos',
  5: 'baculos',
  6: 'escudos',
  7: 'cascos',
  8: 'armaduras',
  9: 'pantalones',
  10: 'guantes',
  11: 'botas',
  12: 'alas-y-orbes',
  13: 'accesorios',
  14: 'joyas-y-consumibles',
  15: 'pergaminos',
}

type Reg = {
  item: Map<string, string>
  itemBySlug: Map<string, string>
  monster: Map<number, string>
  monsterBySlug: Map<string, number>
  map: Map<number, string>
  mapBySlug: Map<string, number>
  spot: Map<string, string>
  spotBySlug: Map<string, string>
}

let reg: Reg | undefined

const sectionOf = (id: string) => Number(id.split('@')[0].split(',')[0])
export const itemCategorySlug = (id: string) => ITEM_SECTION_SLUG[sectionOf(id)] ?? 'otros'

/** Slug único dentro de su grupo: si el nombre se repite, se añade el sufijo alternativo. */
function uniqueSlugs<K>(rows: { key: K; group: string; base: string; alt: string }[]): Map<K, string> {
  const count = new Map<string, number>()
  rows.forEach((r) => count.set(`${r.group}/${r.base}`, (count.get(`${r.group}/${r.base}`) ?? 0) + 1))
  const used = new Set<string>()
  const out = new Map<K, string>()
  rows.forEach((r) => {
    let slug = (count.get(`${r.group}/${r.base}`) ?? 0) > 1 ? `${r.base}-${r.alt}` : r.base
    let n = 2
    while (used.has(`${r.group}/${slug}`)) slug = `${r.base}-${r.alt}-${n++}`
    used.add(`${r.group}/${slug}`)
    out.set(r.key, slug)
  })
  return out
}

const safe = (name: string | undefined, fallback: string) => slugify(name ?? '') || fallback

export function registerWorldRoutes(w: WorldData, isNpc: (m: Monster) => boolean) {
  const itemRows: { key: string; group: string; base: string; alt: string }[] = []
  w.items.forEach((it) => itemRows.push({ key: it.id, group: itemCategorySlug(it.id), base: safe(it.name, it.id.replace(',', '-')), alt: it.id.replace(',', '-') }))
  w.variants.forEach((v) => {
    if (w.items.has(v.id)) return
    const name = variantDisplayNames[v.id]?.displayName ?? v.name
    itemRows.push({ key: v.id, group: itemCategorySlug(v.id), base: safe(name, v.id), alt: v.level !== undefined ? `nivel-${v.level}` : v.id.replace(/[,@]/g, '-') })
  })
  const itemSlugs = uniqueSlugs(itemRows)
  const item = new Map<string, string>()
  const itemBySlug = new Map<string, string>()
  itemSlugs.forEach((slug, id) => {
    const path = `/guia/items/${itemCategorySlug(id)}/${slug}`
    item.set(id, path)
    itemBySlug.set(path, id)
  })

  const monRows: { key: number; group: string; base: string; alt: string }[] = []
  w.monsters.forEach((m) => monRows.push({ key: m.id, group: isNpc(m) ? 'npc' : 'monstruos', base: safe(m.name, `id-${m.id}`), alt: String(m.id) }))
  const monSlugs = uniqueSlugs(monRows)
  const monster = new Map<number, string>()
  const monsterBySlug = new Map<string, number>()
  monRows.forEach((r) => {
    const path = `/guia/${r.group}/${monSlugs.get(r.key)}`
    monster.set(r.key, path)
    monsterBySlug.set(path, r.key)
  })

  const mapRows: { key: number; group: string; base: string; alt: string }[] = []
  w.maps.forEach((m) => mapRows.push({ key: m.id, group: 'mapas', base: safe(m.name, `mapa-${m.id}`), alt: String(m.id) }))
  const mapSlugs = uniqueSlugs(mapRows)
  const map = new Map<number, string>()
  const mapBySlug = new Map<string, number>()
  mapSlugs.forEach((slug, id) => {
    const path = `/guia/mapas/${slug}`
    map.set(id, path)
    mapBySlug.set(path, id)
  })

  // Spots: /guia/mapas/<mapa>/<monstruo>; si el mismo monstruo tiene varios registros en el mapa: -2, -3…
  const spot = new Map<string, string>()
  const spotBySlug = new Map<string, string>()
  const perMap = new Map<string, number>()
  ;[...w.spawns].sort((a, b) => a.id.localeCompare(b.id, 'es', { numeric: true })).forEach((s) => {
    const mapBase = map.get(s.mapId) ?? `/guia/mapas/mapa-${s.mapId}`
    const monBase = safe(w.monsters.get(s.monsterId)?.name, `monstruo-${s.monsterId}`)
    const k = `${mapBase}/${monBase}`
    const n = (perMap.get(k) ?? 0) + 1
    perMap.set(k, n)
    const path = n === 1 ? k : `${k}-${n}`
    spot.set(s.id, path)
    spotBySlug.set(path, s.id)
  })

  reg = { item, itemBySlug, monster, monsterBySlug, map, mapBySlug, spot, spotBySlug }
}

export const prettyItemPath = (id: string) => reg?.item.get(id.trim())
export const prettyMonsterPath = (id: number) => reg?.monster.get(id)
export const prettyMapPath = (id: number) => reg?.map.get(id)
export const prettySpotPath = (id: string) => reg?.spot.get(id)

const clean = (path: string) => decodeURIComponent(path.split(/[?#]/)[0]).replace(/\/+$/, '').toLowerCase()

export const itemIdFromPrettyPath = (path: string) => reg?.itemBySlug.get(clean(path))
export const monsterIdFromPrettyPath = (path: string) => reg?.monsterBySlug.get(clean(path))
export const mapIdFromPrettyPath = (path: string) => reg?.mapBySlug.get(clean(path))
export const spotIdFromPrettyPath = (path: string) => reg?.spotBySlug.get(clean(path))
